import React from "react";
import { Stack, TextField, TooltipHost } from "@fluentui/react";
import { Loading } from "../../../services/Loading";
import { IMorPduReadings } from "../../../vm";
import { ToastContext } from "../../common/ToastProvider";
import { toFixedValue } from "../../../services/UtilService";
import { mdiCheck, mdiClose, mdiPencil } from "@mdi/js";
import { THEME } from "../../../utils/Styles";
import Icon from "@mdi/react";
import { updateCustomerDedicatedPdu } from "../../../services/customers/CustomerService";
import { getMorPduReadings } from "../../../services/infrastructure-automation/InfrastructureAutmationService";
import { string } from "yup";
import moment from "moment";

export interface DedicatedReadingsProps {
  searchObj: any;
}

const DedicatedReadings: React.FC<DedicatedReadingsProps> = ({ searchObj }) => {
  // hooks
  const { showToast } = React.useContext(ToastContext);
  // state
  const [isLoading, setLoading] = React.useState(false);
  const [state, setState] = React.useState({
    isOnEdit: false,
    pduIndex: undefined,
    phaseIndex: undefined,
  } as {
    isOnEdit: boolean;
    pduIndex?: number;
    phaseIndex?: number;
    dataObj: {
      date: Date | string;
      pduId: string;
      breakerId?: string;
      amps?: number | string;
      kw?: number | string;
      kwh?: number | string;
      firstReading?: number | string;
      lastReading?: number | string;
    };
  });
  const [readings, setReadings] = React.useState([] as IMorPduReadings[]);

  // hooks
  React.useEffect(() => {
    const asyncFunc = async () => {
      await searchMorReadings();
    };
    asyncFunc();
  }, [searchObj]);

  const searchMorReadings = async (floorId?: string) => {
    setLoading(true);
    const serchObj = { ...searchObj };
    if (floorId) {
      serchObj["floorId"] = floorId;
    }
    serchObj.startDate = moment(searchObj.startDate).format("YYYY-MM-DD");
    serchObj.endDate = moment(searchObj.endDate).format("YYYY-MM-DD");
    searchObj.isDedicated = true
    // serchObj.startDate = moment(state.date).startOf("month").toDate();
    // serchObj.endDate = moment(state.date).endOf("month").toDate();
    const result = await getMorPduReadings(serchObj);
    if (result && result.success) {
      setState({
        ...state,
        isOnEdit: false,
        pduIndex: undefined,
        phaseIndex: undefined,
        dataObj: {
          ...state.dataObj,
          pduId: "",
          breakerId: "",
          amps: undefined,
          kw: undefined,
          kwh: undefined,
          firstReading: undefined,
          lastReading: undefined,
        },
      });
      setReadings(result.data);
      setLoading(false);
    } else {
      setLoading(false);
      showToast(
        result.message || "Error while fetching sensors readings",
        "error"
      );
    }
  };

  const handleChange = (e: any) => {
    setState({
      ...state,
      dataObj: {
        ...state.dataObj,
        [e.target.name]: e.target.value,
      },
    });
  };

  const onUpdateDedicatedData = async () => {
    console.log("dObj", state.dataObj);
    if (!state.dataObj.firstReading) {
      showToast("First Reading field is required", "error");
    } else if (!state.dataObj.lastReading) {
      showToast("Last Reading is required", "error");
    } else {
      setLoading(true);
      let dataObj = { ...state.dataObj };
      dataObj.date = searchObj.startDate;
      dataObj.firstReading = Number(dataObj.firstReading);
      dataObj.lastReading = Number(dataObj.lastReading);
      let result = await updateCustomerDedicatedPdu(dataObj);
      if (result?.success) {
        let allReadings = [...readings];
        if (state.pduIndex !== undefined) {
          allReadings[state.pduIndex].totalKwh.start =
            result.data?.firstReading || 0;
          allReadings[state.pduIndex].totalKwh.end =
            result.data?.lastReading || 0;
        }
        setReadings(allReadings);
        setState({
          ...state,
          isOnEdit: false,
          pduIndex: undefined,
          dataObj: {
            ...state.dataObj,
            breakerId: "",
            pduId: "",
            kw: undefined,
            amps: undefined,
            kwh: undefined,
            firstReading: undefined,
            lastReading: undefined,
          },
        });
        setLoading(false);
        showToast("Dedicated Pdu updated successfully", "success");
      } else {
        setLoading(false);
        showToast(
          result?.message || "Error while updating dedicated pdu values",
          "error"
        );
      }
    }
  };

  // const removeDuplicateFromArr = (arr: string[]) => {
  //   let tempArr: string[] = [];
  //   arr.forEach((a) => {
  //     if (tempArr.indexOf(a) === -1) {
  //       tempArr.push(a);
  //     }
  //   });
  //   return tempArr.join(", ");
  // };

  return (
    <React.Fragment>
      {isLoading && <Loading />}
      <Stack>
        <Stack.Item className="max-table-width">
          <div className="office-table bg-white">
            <table className="ms-Table bordered-table w-100">
              <thead className="html-table-thead" style={{ fontSize: 14 }}>
                <tr className="table-height-44">
                  <th rowSpan={2}>Pdu No</th>
                  <th rowSpan={2}>Connected Racks</th>
                  <th colSpan={2}>Total Kwh</th>
                  <th rowSpan={2}>Actions</th>
                </tr>
                <tr>
                  <th>First Reading</th>
                  <th>Last Reading</th>
                </tr>
              </thead>
              <tbody>
                {readings && readings.length > 0 ? (
                  readings.reduce((acc: any, d, index) => {
                    const connectedRacks: Array<string> = [];
                    d.phases.forEach((c) => {
                      if (c.connectedTo) {
                        let tempArr = c.connectedTo.split(",");
                        tempArr.forEach((e) => {
                          if (connectedRacks.indexOf(e.trim()) === -1) {
                            connectedRacks.push(e.trim());
                          }
                        });
                      }
                    });

                    acc.push(
                      <tr key={index}>
                        <td>{d.pduName}</td>
                        <td
                          className={
                            connectedRacks.length === 0 ? "bg-warning" : ""
                          }
                        >
                          {connectedRacks.join(", ")}
                          {/* {removeDuplicateFromArr(connectedRacks)} */}
                        </td>
                        {state.isOnEdit && state.pduIndex === index ? (
                          <td width={150}>
                            <TextField
                              value={String(state.dataObj.firstReading)}
                              type="number"
                              name="firstReading"
                              onChange={handleChange}
                              // className="width-100"
                            />
                          </td>
                        ) : (
                          // <td>{d?.totalKwh?.start || "NA"}</td>
                          <td>{toFixedValue(d?.totalKwh?.start)}</td>
                        )}
                        {state.isOnEdit && state.pduIndex === index ? (
                          <td width={150}>
                            <TextField
                              value={String(state.dataObj.lastReading)}
                              type="number"
                              name="lastReading"
                              onChange={handleChange}
                              // className="width-100"
                            />
                          </td>
                        ) : (
                          // <td>{d?.totalKwh?.end || "NA"}</td>
                          <td>{toFixedValue(d?.totalKwh?.end)}</td>
                        )}
                        <td align="center">
                          {state.isOnEdit && state.pduIndex === index ? (
                            <React.Fragment>
                              <TooltipHost
                                content="Save"
                                calloutProps={{
                                  gapSpace: 0,
                                }}
                              >
                                <span onClick={() => onUpdateDedicatedData()}>
                                  <Icon
                                    path={mdiCheck}
                                    size={0.8}
                                    className="pointer"
                                    color="green"
                                  />
                                </span>
                              </TooltipHost>
                              &nbsp;&nbsp;
                              <TooltipHost
                                content="Cancel"
                                calloutProps={{
                                  gapSpace: 0,
                                }}
                              >
                                <span
                                  onClick={() =>
                                    setState({
                                      ...state,
                                      isOnEdit: false,
                                      pduIndex: undefined,
                                      phaseIndex: undefined,
                                      dataObj: {
                                        ...state.dataObj,
                                        firstReading: undefined,
                                        lastReading: undefined,
                                      },
                                    })
                                  }
                                >
                                  <Icon
                                    path={mdiClose}
                                    size={0.8}
                                    className="pointer"
                                    color="red"
                                  />
                                </span>
                              </TooltipHost>
                            </React.Fragment>
                          ) : (
                            <React.Fragment>
                              <TooltipHost
                                content="Edit"
                                calloutProps={{
                                  gapSpace: 0,
                                }}
                              >
                                <span
                                  onClick={() =>
                                    setState({
                                      ...state,
                                      isOnEdit: true,
                                      pduIndex: index,
                                      dataObj: {
                                        ...state.dataObj,
                                        pduId: d?.pduId || "",
                                        firstReading:
                                          d?.totalKwh?.start &&
                                          d.totalKwh.start !== "NA"
                                            ? d.totalKwh.start
                                            : "0",
                                        lastReading:
                                          d?.totalKwh.end &&
                                          d.totalKwh.end !== "NA"
                                            ? d.totalKwh.end
                                            : "0",
                                      },
                                    })
                                  }
                                >
                                  <Icon
                                    path={mdiPencil}
                                    size={0.7}
                                    className="pointer"
                                    color={THEME.palette.themePrimary}
                                  />
                                </span>
                              </TooltipHost>
                            </React.Fragment>
                          )}
                        </td>
                      </tr>
                    );

                    return acc;
                  }, [])
                ) : (
                  <tr>
                    <td colSpan={5} align="center">
                      No Data Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Stack.Item>
      </Stack>
    </React.Fragment>
  );
};

export default DedicatedReadings;
